import type { ListResponse, MinDateMaxDate, Union } from "@/base/types"
import type { AddDeviceErrorCode, AddDeviceInputMode } from "@/constants/base"
import type {
  DevicePermissions,
  FarmUserPermissionName,
  FarmUserPermissions,
  FarmWithPermissions,
} from "@/constants/permissions"
import type { FormValidationInfo } from "@/hooks/useFormvalidation"
import type { CenterPivotPayload } from "@/redux/center-pivots.reducer"
import type { LinearIrrigatorPayload } from "@/redux/linear-irrigators.reducer"
import type { DeviceConfiguration } from "@/redux/device-configurations.reducer"
import type { TriggerFilterKey } from "@/redux/device-connections.reducer"
import type { FieldType } from "@/redux/farm-fields.reducer"
import type {
  FlowAnalyticsData,
  FlowAnalyticsSource,
} from "@/redux/farm.reducer"
import type { FarmAccount, FarmAccountProperties } from "@/redux/models"
import type { JoinFarmErrorCode } from "@/redux/user-farms.reducer"
import type { NotificationsStatus } from "@/redux/user-phone-numbers.reducer"
import type { SupportedAppLanguageCode } from "@/translations/i18n"

import { Platform } from "react-native"

import { denyPermission } from "@/redux/actions"
import { createAsyncThunk } from "@/redux/create-async-thunk"
import * as Models from "@/redux/models"
import {
  getDemoFarmIdOrActiveFarmIdFromState,
  getIsAdminModeEnabledFromState,
  isDevicePair,
} from "@/redux/selectors"
import { isDemoFarmId } from "@/redux/user-farms.selectors"
import i18n from "@/translations/i18n"
import * as Sentry from "@sentry/react-native"

import * as AppStorage from "./async-storage"
import { ENVIRONMENT, isTargetDatabase } from "./environment"
import { logger, makeFileLogger } from "./logger"
import { trackEvent } from "./segment"
import { createHandler, sendRequest } from "./send-request"

import type * as Geo from "./geo"
import type {
  ApplicationType,
  InstallationType,
  MutableSensorName,
  SensorConfig,
  SensorName,
} from "./sensor-configurations"
import type { SensorState } from "./sensor-events"

import type { SeedDatabaseOptions } from "./seed-test-database"
import type {
  BackendRequest,
  GetActionArgumentsType,
  GetResponseDataType,
} from "./send-request"

const fileLogger = makeFileLogger({ fileName: "farmhq-api.ts" })

/**
 *
 */
export const acceptTermsOfServiceAsync = createHandler(
  "AcceptTermsOfService",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 *
 */
export const activateFieldsAsync = createHandler("ActivateFields", {
  requiredPermissions: "canManageFields",
})

export type AddDeviceResponse =
  | {
      deviceConfiguration: DeviceConfiguration | null
      deviceConnections: Models.EventActionTrigger[] | null
      deviceEvent: Models.DeviceEvent | null
      deviceFarmAssociation: Models.DeviceFarmAssociation | null
      deviceShareCode?: Models.DeviceShareCode | null
      status: "VALID"
    }
  | {
      status: AddDeviceErrorCode
    }

// API responses ship the raw flat-boundary shape; consumers normalize via
// `prepareCenterPivot` before storing in redux.
export type CenterPivotResponse = CenterPivotPayload

// API responses ship the raw flat-boundary shape; consumers normalize via
// `prepareLinearIrrigator` before storing in redux.
export type LinearIrrigatorResponse = LinearIrrigatorPayload

// Irrigation Plan Response Types
import type {
  IrrigationPlan,
  PlanSector,
  BoundaryAction,
  PlanExecutionLog,
} from "@/redux/irrigation-plans.reducer"
import type {
  PivotEndGunTable,
  PivotEndGunTableSector,
} from "@/redux/pivot-end-gun-tables.reducer"
import type { EndGunState } from "@/redux/pivot-end-gun-tables.reducer"

// Field names match what camelcaseKeys produces from the serializer response.
// camelcaseKeys merges single-letter uppercase segments (O, I, A, B) with
// adjacent words, e.g. centerPivotOFwdAddr -> centerPivotOfwdAddr.
export interface CenterPivotSensorConfig {
  id: number
  sensorType: string
  // Outputs
  centerPivotOfwdAddr: number | null
  centerPivotOfwdPin: number | null
  centerPivotOrvsAddr: number | null
  centerPivotOrvsPin: number | null
  centerPivotOspeedControlAaddr: number | null
  centerPivotOspeedControlApin: number | null
  centerPivotOspeedControlBaddr: number | null
  centerPivotOspeedControlBpin: number | null
  centerPivotOendGunAddr: number | null
  centerPivotOendGunPin: number | null
  centerPivotOenableAddr: number | null
  centerPivotOenablePin: number | null
  centerPivotOwaterAddr: number | null
  centerPivotOwaterPin: number | null
  centerPivotOemergencyShutdownAddr: number | null
  centerPivotOemergencyShutdownPin: number | null
  centerPivotOsftyOvrdAddr: number | null
  centerPivotOsftyOvrdPin: number | null
  // Inputs
  centerPivotIfwdAddr: number | null
  centerPivotIfwdPin: number | null
  centerPivotIrvsAddr: number | null
  centerPivotIrvsPin: number | null
  centerPivotIspeedControlAaddr: number | null
  centerPivotIspeedControlApin: number | null
  centerPivotIspeedControlBaddr: number | null
  centerPivotIspeedControlBpin: number | null
  centerPivotIendGunAddr: number | null
  centerPivotIendGunPin: number | null
  centerPivotIrvsStpAddr: number | null
  centerPivotIrvsStpPin: number | null
  centerPivotIauxSpdAddr: number | null
  centerPivotIauxSpdPin: number | null
  centerPivotIspdAddr: number | null
  centerPivotIspdPin: number | null
  centerPivotIrunAddr: number | null
  centerPivotIrunPin: number | null
  centerPivotIwaterAddr: number | null
  centerPivotIwaterPin: number | null
  centerPivotIenableAddr: number | null
  centerPivotIenablePin: number | null
  centerPivotIsftyAddr: number | null
  centerPivotIsftyPin: number | null
  centerPivotIsftyOvrdAddr: number | null
  centerPivotIsftyOvrdPin: number | null
  centerPivotIemergencyShutdownAddr: number | null
  centerPivotIemergencyShutdownPin: number | null
  // Pulse timing
  centerPivotPulseSpeedControlAms: number | null
  centerPivotPulseSpeedControlBms: number | null
  // Speed controls
  centerPivotSpeedControlsType: string | null
  centerPivotValveMaxOpenMs: number | null
  // Speed feedback. The port and the calibration are an all-or-nothing
  // pair — the controller only builds its speed sensor when it has
  // both, and reports no error when it doesn't.
  centerPivotSpeedPulsePort: number | null
  /** Linear meters travelled per pulse. Calibrate this so the resulting
   * m/min reads as TIP speed, matching the pivot's min/max
   * meters-per-hour envelope — the signal itself is piped in from
   * whatever the install provides, so this is what defines the frame
   * the measurement is reported in. */
  centerPivotMetersPerPulse: number | null
  /** Measurement window in ms, which is also the closed-loop
   * regulator's sample period. Null/0 lets the controller default:
   * 5000 for valve, 60000 for duty cycle (which also floors there). */
  centerPivotSpeedSampleWindowMs: number | null
  // Safety / graceful-stop timing
  centerPivotSafetyDelaySeconds: number | null
  centerPivotShutdownGraceSeconds: number | null
}

export type IrrigationPlanResponse = IrrigationPlan
export type PlanSectorResponse = PlanSector
export type BoundaryActionResponse = BoundaryAction
export type PlanExecutionLogResponse = PlanExecutionLog

// Pivot End Gun Table Response Types
export type PivotEndGunTableResponse = PivotEndGunTable
export type PivotEndGunTableSectorResponse = PivotEndGunTableSector

export interface IrrigationPlanSummaryResponse {
  id: number
  name: string
  description: string | null
  centerPivotId: number
  farmId: number
  state: string
  activatedAt: string | null
  sectorCount: number
  boundaryActionCount: number
  isActive: boolean
  createDate: string
}

export interface IrrigationPlanSectorInput {
  name?: string | null
  startAngleDegrees: number
  endAngleDegrees: number
  priority?: number
  irrigationMode?: string
  depthMode?: string
  speedPercent?: number | null
  applicationDepthInches?: number | null
  waterState?: string
  endGunState?: string
  direction?: string
  color?: string
}

export interface IrrigationPlanBoundaryActionInput {
  name?: string | null
  triggerAngleDegrees: number
  triggerDirection?: string
  actionType: string
  targetAngleDegrees?: number | null
  delaySeconds?: number
  customActionId?: number | null
}

export interface PivotEndGunTableSectorInput {
  startAngleDegrees: number
  endAngleDegrees: number
  endGunState?: EndGunState
  sortOrder?: number
}

// function cleanInput(value: string) {
//   return value
//     .toLocaleLowerCase()
//     .trim()
//     .replace(/\s+/g, "-")
//     .replace(/_/g, "-")
// }

/**
 *
 */
export const addDeviceAsync = createHandler("AddDevice", {
  transformArguments: ({ actionArguments }) => {
    return {
      ...actionArguments,
      value: actionArguments.value
        .toLocaleLowerCase()
        .trim()
        .replace(/\s+/g, "-")
        .replace(/_/g, "-"),
    }
  },
})

/**
 *
 */
export const archiveFieldsAsync = createHandler("ArchiveFields", {
  requiredPermissions: "canManageFields",
})

/**
 *
 */
export const changeFarmLocationAsync = createHandler("ChangeFarmLocation")

/**
 * Create a new CenterPivot entity
 */
export const createCenterPivotAsync = createHandler("CreateCenterPivot", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Create a new LinearIrrigator entity. Linear-move analogue of
 * ``createCenterPivotAsync``.
 */
export const createLinearIrrigatorAsync = createHandler(
  "CreateLinearIrrigator",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Start or stop a center pivot run, with the durable run-lifecycle
 * (CenterPivotPlan + monitor activity bookkeeping). Replaces direct
 * cp_controller_set calls for the start/stop transitions; mid-run
 * tweaks (speed, end-gun, pump) still go through CallNamedDeviceAction.
 */
export const controlCenterPivotAsync = createHandler("ControlCenterPivot", {
  requiredPermissions: ["canControlDevicesRemotely"],
})

/**
 * Linear-move analogue of ``controlCenterPivotAsync``. Same dispatch
 * semantics — the backend wraps the same ``cp_controller_set`` device
 * action. Distance along the path replaces angle around a center.
 */
export const controlLinearIrrigatorAsync = createHandler(
  "ControlLinearIrrigator",
  {
    requiredPermissions: ["canControlDevicesRemotely"],
  },
)

/**
 * Delete (soft) a CenterPivot entity
 */
export const deleteCenterPivotAsync = createHandler("DeleteCenterPivot", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Delete (soft) a LinearIrrigator entity. Linear-move analogue of
 * ``deleteCenterPivotAsync``. Unlinks any device configurations that
 * still point at it before flipping is_active=False on the parent.
 */
export const deleteLinearIrrigatorAsync = createHandler(
  "DeleteLinearIrrigator",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Get a single CenterPivot by ID
 */
export const getCenterPivotAsync = createHandler("GetCenterPivot")

/**
 * Set a device's installation type to center_pivot_controller and link it to a pivot
 */
export const linkCenterPivotControllerAsync = createHandler(
  "LinkCenterPivotController",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Set a device's installation type to center_pivot_monitor and link it to a pivot
 */
export const linkCenterPivotMonitorAsync = createHandler(
  "LinkCenterPivotMonitor",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Link a device configuration to a CenterPivot
 */
export const linkDeviceToCenterPivotAsync = createHandler(
  "LinkDeviceToCenterPivot",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Link a device configuration to a LinearIrrigator. The device must
 * already have an installation type of center_pivot_controller (linears
 * reuse the pivot controller firmware) or linear_move_monitor.
 */
export const linkDeviceToLinearIrrigatorAsync = createHandler(
  "LinkDeviceToLinearIrrigator",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * List all CenterPivots for the active farm
 */
export const listCenterPivotsAsync = createHandler("ListCenterPivots")

/**
 * Save center pivot controller I/O pin configuration
 */
export const saveCenterPivotControllerConfigAsync = createHandler(
  "SaveCenterPivotControllerConfig",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Unlink a device configuration from its CenterPivot
 */
export const unlinkDeviceFromCenterPivotAsync = createHandler(
  "UnlinkDeviceFromCenterPivot",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Unlink a device configuration from its LinearIrrigator. Linear
 * analogue of ``unlinkDeviceFromCenterPivotAsync``.
 */
export const unlinkDeviceFromLinearIrrigatorAsync = createHandler(
  "UnlinkDeviceFromLinearIrrigator",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Update a CenterPivot entity
 */
export const updateCenterPivotAsync = createHandler("UpdateCenterPivot", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Update a LinearIrrigator entity. Linear-move analogue of
 * ``updateCenterPivotAsync``.
 */
export const updateLinearIrrigatorAsync = createHandler(
  "UpdateLinearIrrigator",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Reconcile a CenterPivot's slot rows against a target list. Slots with an
 * `id` matching an existing row are updated; entries without `id` are
 * created; existing slots whose id is absent from the list are deleted.
 */
export const setCenterPivotSlotsAsync = createHandler("SetCenterPivotSlots", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Reconcile a LinearIrrigator's slot rows against a target list. Same
 * upsert/delete semantics as ``setCenterPivotSlotsAsync``.
 */
export const setLinearIrrigatorSlotsAsync = createHandler(
  "SetLinearIrrigatorSlots",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

// =============================================================================
// IRRIGATION PLAN HANDLERS
// =============================================================================

/**
 * Create a new IrrigationPlan for a center pivot
 */
export const createIrrigationPlanAsync = createHandler("CreateIrrigationPlan", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Update an IrrigationPlan
 */
export const updateIrrigationPlanAsync = createHandler("UpdateIrrigationPlan", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Delete (soft) an IrrigationPlan
 */
export const deleteIrrigationPlanAsync = createHandler("DeleteIrrigationPlan", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Get a single IrrigationPlan by ID
 */
export const getIrrigationPlanAsync = createHandler("GetIrrigationPlan")

/**
 * List all irrigation plans for a center pivot or farm
 */
export const listIrrigationPlansAsync = createHandler("ListIrrigationPlans")

/**
 * Create a sector within an irrigation plan
 */
export const createPlanSectorAsync = createHandler("CreatePlanSector", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Update a sector within an irrigation plan
 */
export const updatePlanSectorAsync = createHandler("UpdatePlanSector", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

// =============================================================================
// PIVOT END GUN TABLE HANDLERS
// =============================================================================

export const listPivotEndGunTablesAsync = createHandler("ListPivotEndGunTables", {
  requiredPermissions: ["canManageDeviceConfiguration"],
  demoMode: { allowRequest: true },
})

export const createPivotEndGunTableAsync = createHandler("CreatePivotEndGunTable", {
  requiredPermissions: ["canManageDeviceConfiguration"],
  demoMode: { allowRequest: true },
})

export const updatePivotEndGunTableAsync = createHandler("UpdatePivotEndGunTable", {
  requiredPermissions: ["canManageDeviceConfiguration"],
  demoMode: { allowRequest: true },
})

export const deletePivotEndGunTableAsync = createHandler("DeletePivotEndGunTable", {
  requiredPermissions: ["canManageDeviceConfiguration"],
  demoMode: { allowRequest: true },
})

export const listPivotEndGunTableSectorsAsync = createHandler(
  "ListPivotEndGunTableSectors",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
    demoMode: { allowRequest: true },
  },
)

export const createPivotEndGunTableSectorAsync = createHandler(
  "CreatePivotEndGunTableSector",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
    demoMode: { allowRequest: true },
  },
)

export const updatePivotEndGunTableSectorAsync = createHandler(
  "UpdatePivotEndGunTableSector",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
    demoMode: { allowRequest: true },
  },
)

export const deletePivotEndGunTableSectorAsync = createHandler(
  "DeletePivotEndGunTableSector",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
    demoMode: { allowRequest: true },
  },
)

export const replacePivotEndGunTableSectorsAsync = createHandler(
  "ReplacePivotEndGunTableSectors",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
    demoMode: { allowRequest: true },
  },
)

/**
 * Delete a sector from an irrigation plan
 */
export const deletePlanSectorAsync = createHandler("DeletePlanSector", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Replace all sectors in an irrigation plan atomically.
 * This simplifies editing by deleting all existing sectors and creating
 * new ones in a single transaction.
 */
export const replaceIrrigationPlanSectorsAsync = createHandler(
  "ReplaceIrrigationPlanSectors",
  {
    requiredPermissions: ["canManageDeviceConfiguration"],
  },
)

/**
 * Create a boundary action for an irrigation plan
 */
export const createBoundaryActionAsync = createHandler("CreateBoundaryAction", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Update a boundary action
 */
export const updateBoundaryActionAsync = createHandler("UpdateBoundaryAction", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Delete a boundary action
 */
export const deleteBoundaryActionAsync = createHandler("DeleteBoundaryAction", {
  requiredPermissions: ["canManageDeviceConfiguration"],
})

/**
 * Activate an irrigation plan for execution
 */
export const activateIrrigationPlanAsync = createHandler(
  "ActivateIrrigationPlan",
  {
    requiredPermissions: ["canControlDevicesRemotely"],
  },
)

/**
 * Pause an active irrigation plan
 */
export const pauseIrrigationPlanAsync = createHandler("PauseIrrigationPlan", {
  requiredPermissions: ["canControlDevicesRemotely"],
})

/**
 * Resume a paused irrigation plan
 */
export const resumeIrrigationPlanAsync = createHandler("ResumeIrrigationPlan", {
  requiredPermissions: ["canControlDevicesRemotely"],
})

/**
 * Stop and complete an irrigation plan
 */
export const stopIrrigationPlanAsync = createHandler("StopIrrigationPlan", {
  requiredPermissions: ["canControlDevicesRemotely"],
})

/**
 * Get execution logs for an irrigation plan
 */
export const getPlanExecutionLogsAsync = createHandler("GetPlanExecutionLogs")

// =============================================================================
// SMS & VERIFICATION HANDLERS
// =============================================================================

/**
 *
 */
export const completeSmsVerificationAsync = createHandler(
  "CompleteSmsVerification",
)

/**
 *
 */
export const createDeviceShareCodeAsync = createHandler(
  "CreateDeviceShareCode",
)

//   requiredPermissions: ["canManageDevicePairs"],
//  *
//  */
// /**
// export const createDevicePairAsync = createHandler("CreateDevicePair", {
// })
/**
 *
 */
export const createCustomAutomationAsync = createHandler(
  "CreateCustomAutomation",
  {
    requiredPermissions: ["canManageCustomTriggers"],
  },
)

/**
 *
 */
export const createCustomNotificationAsync = createHandler(
  "CreateCustomNotification",
  {
    requiredPermissions: ["canManageDeviceNotifications"],
  },
)

/**
 *
 */
export const createFarmAsync = createHandler("CreateFarm")

/**
 *
 */
export const createFarmJoinCodeAsync = createHandler("CreateFarmJoinCode", {
  requiredPermissions: "canManageJoinCodes",
})

/**
 *
 */
export const generateSmsVerificationCodeAsync = createHandler(
  "GenerateSmsVerificationCode",
  {
    demoMode: {
      allowRequest: false,
    },
    transformArguments: ({ actionArguments }) => {
      if (Platform.OS === "web") {
        return {
          ...actionArguments,
          isTest: Boolean(window.Cypress),
        }
      }
      return actionArguments
    },
  },
)

/**
 *
 */
export const createPhoneNumberInternationalAsync = createHandler(
  "CreatePhoneNumberInternational",
  {
    demoMode: {
      allowRequest: false,
    },
  },
)

/**
 *
 */
export const createConfigurationAsync = createHandler("CreateConfiguration", {
  requiredPermissions: "canManageDeviceConfiguration",
})

/**
 *
 */
export const createFieldAsync = createHandler("CreateField", {
  requiredPermissions: "canManageFields",
})

/**
 *
 */
export const createSupportTicketAsync = createAsyncThunk<
  GetResponseDataType<"CreateSupportTicket">,
  GetActionArgumentsType<"CreateSupportTicket">
>("CreateSupportTicket", async ({ body, email, subject }, { getState }) => {
  const activeFarmId = getDemoFarmIdOrActiveFarmIdFromState(getState())
  try {
    // Only actually create tickets in production or beta
    if (isTargetDatabase("PROD", "BETA") || ENVIRONMENT.isEndToEndTest) {
      await sendRequest("CreateSupportTicket", {
        actionArguments: { body, email, subject },
        activeFarmId,
      })
      return { requestSkipped: false }
    }
    return { requestSkipped: true }
  } catch (error) {
    fileLogger.error(error)
    throw error
  } finally {
    trackEvent({
      // activeFarmId,
      // environmentInfo: extra.environmentInfo,
      name: "create_support_ticket",
      subject,
    })
  }
})

/**
 *
 */
export const customizeNotificationStringAsync = createHandler(
  "CustomizeNotificationString",
  { requiredPermissions: "canManageDeviceNotifications" },
)

/**
 *
 */
export const deleteEventActionTriggersAsync = createHandler(
  "DeleteEventActionTriggers",
  {
    requiredPermissions: (actionArguments, state) => {
      const required: FarmUserPermissionName[] = []
      for (const triggerId of actionArguments.ids) {
        const trigger = Models.deviceConnection.selectById(state, triggerId)
        if (trigger) {
          // if (isDevicePair(trigger)) {
          //   required.push("canManageDevicePairs")
          // } else
          if (trigger.namedDeviceAction !== null) {
            required.push("canManageCustomTriggers")
          } else if (
            (trigger as Models.EventActionTriggerProperties).notify === true
          ) {
            required.push("canManageDeviceNotifications")
          }
        }
      }
      return required
    },
  },
)

/**
 *
 */
export const deleteFieldAsync = createHandler("DeleteField", {
  requiredPermissions: "canManageFields",
})

/**
 *
 */
export const deletePhoneNumberAsync = createHandler("DeletePhoneNumber", {})

/**
 *
 */
export const loadFarmPermissionsForUserAsync = createHandler(
  "GetFarmPermissionsForUser",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 *
 */
export const loadFarmsBorrowingDeviceAsync = createHandler(
  "GetFarmsBorrowingDevice",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 *
 */
export const loadLastEventForDeviceAsync = createHandler(
  "GetLastEventForDevice",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 *
 */
export const invalidateDeviceShareCodeAsync = createHandler(
  "InvalidateDeviceShareCode",
  {
    requiredPermissions: ["canManageDevicePermissions"],
  },
)

/**
 *
 */
export const loadAppNotificationsAsync = createHandler("GetNotifications", {
  demoMode: {
    allowRequest: true,
  },
})

/** AI assistant request thunks. */
export const askAssistantAsync = createHandler("AskAssistant")
export const getAssistantJobAsync = createHandler("GetAssistantJob")
export const getAssistantUsageAsync = createHandler("GetAssistantUsage")
export const listAssistantConversationsAsync = createHandler(
  "ListAssistantConversations",
)
export const getAssistantConversationAsync = createHandler(
  "GetAssistantConversation",
)
export const getAssistantReportAsync = createHandler("GetAssistantReport")

/**
 *
 */
export const loadDeviceActivityAsync = createHandler("LoadDeviceActivity", {
  demoMode: {
    allowRequest: true,
  },
  // sometimes created_at_timestamp is unreliable but we dont want to
  // filter out future device events
  transformArguments: ({ actionArguments }) => ({
    ...actionArguments,
    maxDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  }),
})

/**
 *
 */
export const loadDeviceFunctionCallsAsync = createHandler(
  "LoadDeviceFunctionCalls",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export const loadDeviceTimeseriesDataAsync = createHandler(
  "LoadDeviceTimeseriesData",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 *
 */
export const loadDeviceFlowHistoryDataAsync = createHandler(
  "LoadDeviceFlowHistoryData",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 *
 */
export const loadEventsForDeviceAsync = createHandler("LoadEventsForDevice", {
  demoMode: {
    allowRequest: true,
  },
})

// `loadFarmTimelineEventsAsync` is defined in `@/redux/farm-timeline.reducer`
// rather than here to avoid a Metro circular-import that left the symbol
// undefined when this slot of `farmhq-api.ts` had not yet evaluated at
// reducer-init time. Import it directly from the reducer module.

/**
 *
 */
export const loadNotificationsAsync = createHandler("GetNotifications", {
  demoMode: {
    allowRequest: true,
  },
})

/**
 *
 */
export const loadFieldIrrigationHistoryAsync = createHandler(
  "LoadFieldIrrigationHistory",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export const loadFieldIrrigationHeatmapAsync = createHandler(
  "LoadFieldIrrigationHeatmap",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export const loadCenterPivotIrrigationHeatmapAsync = createHandler(
  "LoadCenterPivotIrrigationHeatmap",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export const exportFieldIrrigationHistoryAsync = createHandler(
  "ExportFieldIrrigationHistory",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

/**
 * Manually expire the backend's cached LoadApp view for the active farm so
 * the next load recalculates it.
 */
export const expireFarmViewCacheAsync = createHandler("ExpireFarmViewCache")

/**
 *
 */
export const removeDeviceFromFarmAsync = createHandler("RemoveDeviceFromFarm")

/**
 *
 */
export const renameDeviceAsync = createHandler("RenameDevice", {
  requiredPermissions: "canManageDeviceConfiguration",
})

/**
 *
 */
export const renameFieldAsync = createHandler("RenameField", {
  requiredPermissions: "canManageFields",
})

export const replaceDeviceAsync = createHandler("ReplaceDevice", {
  requiredPermissions: "canManageDeviceConfiguration",
})
/**
 *
 */
export const requestUpdateFromDeviceAsync = createHandler(
  "RequestUpdateFromDevice",
)

export const resetDeviceAsync = createHandler("ResetDevice", {
  requiredPermissions: ["canControlDevicesRemotely"],
})

/**
 * Return true if provided object's device id matches the source or target
 * of the trigger.
 */
export function isDeviceInTrigger({
  connection: { sourceDeviceId, targetDeviceId },
  device: { deviceId },
}: {
  connection: Pick<
    Models.EventActionTrigger,
    "sourceDeviceId" | "targetDeviceId"
  >
  device: Models.DeviceIdProps
}): boolean {
  const isSource =
    typeof sourceDeviceId === "string" && deviceId === sourceDeviceId
  const isTarget =
    typeof targetDeviceId === "string" && deviceId === targetDeviceId
  return isSource || isTarget
}

/**
 *
 */
export const restoreConfigurationDefaultsAsync = createAsyncThunk(
  "RestoreConfigurationDefaults",
  async ({ deviceId }: Models.DeviceIdProps, { dispatch, getState }) => {
    try {
      const state = getState()
      const activeFarmId = getDemoFarmIdOrActiveFarmIdFromState(state)

      const deviceConnections = Models.deviceConnection
        .selectAll(state)
        .filter((trigger) =>
          isDeviceInTrigger({ connection: trigger, device: { deviceId } }),
        )
      const hasTriggers = deviceConnections.length > 0
      const permissions = state.userPermissions.farmUserPermissions

      // let hasPairs = false
      let hasAutomations = false
      for (const trigger of deviceConnections) {
        if (isDevicePair(trigger)) {
          // hasPairs = true
        } else {
          hasAutomations = true
        }
      }

      // if (hasPairs && !Boolean(permissions?.canManageDevicePairs)) {
      //   dispatch(
      //     denyPermission({
      //       missingPermissions: "canManageDevicePairs",
      //       reason: "missing permissions",
      //     }),
      //   )
      //   return Promise.resolve()
      // }

      if (hasAutomations && !Boolean(permissions?.canManageCustomTriggers)) {
        dispatch(
          denyPermission({
            code: "MISSING_PERMISSIONS",
            missingPermissions: ["canManageCustomTriggers"],
          }),
        )
        return Promise.resolve()
      }
      if (hasTriggers) {
        sendRequest("DeleteEventActionTriggers", {
          actionArguments: {
            deviceId,
            ids: deviceConnections.map((trigger) => trigger.id),
            kind: "pair",
          },
          activeFarmId,
        }).catch((error) => {
          logger.error(error)
        })
      }
      const result = await sendRequest("RestoreConfigurationDefaults", {
        actionArguments: { deviceId },
        activeFarmId,
      }).catch((error) => {
        logger.error(error)
      })

      return {
        deviceId,
        result,
        status: "success",
      }
    } catch (error) {
      logger.error(error)
      throw error
    }
  },
)

/**
 *
 */
export const sendTestSmsAsync = createHandler("SendTestSms")

/**
 * Send a test notification to the signed-in user only.
 */
export const sendTestNotificationAsync = createHandler("SendTestNotification")

/**
 * Send a test notification to every member of the active farm.
 */
export const sendFarmTestNotificationAsync = createHandler(
  "SendFarmTestNotification",
  { requiredPermissions: ["canManageDeviceNotifications"] },
)

/**
 * Send a test notification to everyone configured for a single device.
 */
export const sendDeviceTestNotificationAsync = createHandler(
  "SendDeviceTestNotification",
  { requiredPermissions: ["canManageDeviceNotifications"] },
)

/**
 *
 */
export const setDeviceLocationPermanentAsync = createHandler(
  "SetDeviceLocationPermanent",
  { requiredPermissions: "canManageDeviceConfiguration" },
)

export const setDeviceMuteStatusAsync = createHandler("SetDeviceMuteStatus", {
  requiredPermissions: ["canManageDeviceNotifications"],
})

export const clearDeviceUnreachableAsync = createHandler(
  "ClearDeviceUnreachable",
)

export const setNotificationChannelPreferenceAsync = createHandler(
  "SetNotificationChannelPreference",
)

/**
 *
 */
export const setFieldRowDirectionAsync = createHandler(
  "SetFieldRowDirection",
  {
    requiredPermissions: "canManageFields",
  },
)

/**
 *
 */
export const setNotificationStatusForPhoneNumberAsync = createHandler(
  "SetNotificationStatusForPhoneNumber",
)

/**
 *
 */
export const setReelDistanceAsync = createHandler("SetReelDistance")

/**
 *
 */
export const setReelPumpPairRelayToggleDurationAsync = createHandler(
  "SetReelPumpPairRelayToggleDuration",
)

/**
 *
 */
export const setReelRunDirectionAsync = createHandler("SetReelRunDirection")

/**
 *
 */
export const setReelRunApplicationTypeAsync = createHandler(
  "SetReelRunApplicationType",
)

/**
 *
 */
export const setReelRunFieldAsync = createHandler("SetReelRunField")

/**
 *
 */
export const submitAppFeatureFeedbackAsync = createHandler(
  "SubmitAppFeatureFeedback",
)

/**
 *
 */
export const toggleNotificationsFromDevicesAsync = createHandler(
  "ToggleNotificationsFromDevices",
)

/**
 *
 */
export const updateFarmUserPermissionsAsync = createHandler(
  "UpdateFarmUserPermissions",
  {
    demoMode: {
      allowRequest: false,
    },
    requiredPermissions: "canManageUserPermission",
  },
)

/**
 *
 */
export const updateFieldBoundaryAsync = createHandler("UpdateFieldBoundary", {
  requiredPermissions: "canManageFields",
})

/**
 *
 */
export const userJoinFarmWithCodeAsync = createAsyncThunk<
  GetResponseDataType<"UserJoinFarmWithCode">,
  GetActionArgumentsType<"UserJoinFarmWithCode">
>("UserJoinFarmWithCode", async ({ codeString }) => {
  try {
    codeString = codeString.trim().toLocaleUpperCase()
    const response = await sendRequest("UserJoinFarmWithCode", {
      actionArguments: { codeString },
      activeFarmId: undefined,
    })

    return response
  } catch (error) {
    fileLogger.error(error)
    throw error
  }
})

/**
 *
 */
export const callNamedDeviceActionAsync = createHandler(
  "CallNamedDeviceAction",
  {
    thunkOptions: {
      condition: ({ deviceId }, { getState }) => {
        const prevState = getState().deviceCommands.entities[deviceId]
        if ((prevState?.forceUpdateCount ?? 0) > 0) {
          return false
        }
        return undefined
      },
      dispatchConditionRejection: false,
    },
  },
)

/**
 *
 */
export const setNotificationReadStatusAsync = createHandler(
  "SetNotificationReadStatus",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export type SmsVerificationStatusCode = "INVALID" | "SUCCESS"
export const loadOtherFarmUsersAsync = createHandler("LoadOtherFarmUsers", {
  demoMode: {
    allowRequest: true,
  },
})

export const loadActiveUserAsync = createAsyncThunk<
  GetResponseDataType<"LoadActiveUser">,
  GetActionArgumentsType<"LoadActiveUser">
>("LoadActiveUser", async () => {
  const response = await sendRequest("LoadActiveUser", {
    actionArguments: undefined,
    activeFarmId: undefined,
  }).catch((error) => {
    fileLogger.error(error)
    throw error
  })

  const {
    farms,
    userData: { isAdmin, preferredLanguageCodeOverride },
  } = response

  Sentry.setTag("is_admin_user", isAdmin)
  if (isAdmin !== true && !__DEV__ && isTargetDatabase("PROD")) {
    logger.disable()
  }
  if (!Boolean(isAdmin)) {
    fileLogger.debug("Removing admin mode from storage...")
    AppStorage.removeItem("isAdminModeEnabled").catch((error) =>
      fileLogger.error(error),
    )
  }
  const { nameFamily, nameGiven } = response.userData
  Sentry.setTag("user_name_family", nameFamily)
  Sentry.setTag("user_name_given", nameGiven)

  if (typeof preferredLanguageCodeOverride === "string") {
    Sentry.setTag(
      "preferred_language_code_override",
      preferredLanguageCodeOverride,
    )
    fileLogger.debug(`Setting language to ${preferredLanguageCodeOverride}`)
    i18n.changeLanguage(preferredLanguageCodeOverride).catch((error) =>
      Sentry.captureException(error, {
        tags: {
          description: "attempting to override device locale",
          requested_action: "LoadActiveUser",
        },
      }),
    )
  }
  if (farms.length === 0) {
    fileLogger.warn("Clearing stored farm ids...")
    AppStorage.clearStoredFarmIds().catch((error) => fileLogger.error(error))
  }
  return response
})

/**
 *
 */
export const prepareDemoEnvironmentAsync = createAsyncThunk<
  { activeFarmId: number },
  undefined
>("PrepareDemoEnvironment", async (_, { dispatch }) => {
  try {
    const response = await sendRequest("PrepareDemoEnvironment", {
      actionArguments: undefined,
      activeFarmId: undefined,
    })

    const demoFarmId = response.activeFarmId
    if (!isDemoFarmId(demoFarmId)) {
      throw new TypeError(
        `Farm ID ${demoFarmId} is not a demo farm id: ${demoFarmId}`,
      )
    }
    // This causes demo mode to persist
    await AppStorage.setItem({ key: "demoFarmId", value: demoFarmId })
    // This ensures that the user has an association to the demo farm
    // when the request is finished
    await dispatch(loadActiveUserAsync())
    return {
      activeFarmId: demoFarmId,
    }
  } catch (error) {
    fileLogger.error(error)
    throw error
  }
})
/**
 *
 */
export const loadDeviceActionsAsync = createHandler("LoadDeviceActions", {
  demoMode: {
    allowRequest: true,
  },
})

export const loadFlowMeteringAnalyticsAsync = createHandler(
  "LoadFlowMeteringAnalytics",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export const loadFlowMeteringReportAsync = createHandler(
  "LoadFlowMeteringReport",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

export const createScheduledDeviceActionAsync = createHandler(
  "CreateScheduledDeviceAction",
  {
    requiredPermissions: "canManageSchedules",
  },
)

export const createDeviceActionScheduleAsync = createHandler(
  "CreateDeviceActionSchedule",
  {
    requiredPermissions: "canManageSchedules",
  },
)
export const createPumpOnForDurationScheduleAsync = createHandler(
  "CreatePumpOnForDurationSchedule",
  {
    requiredPermissions: "canManageSchedules",
  },
)
export const deleteScheduledDeviceActionAsync = createHandler(
  "DeleteDeviceActionSchedule",
  {
    requiredPermissions: "canManageSchedules",
  },
)

export const updateScheduledDeviceActionAsync = createHandler(
  "UpdateScheduledDeviceAction",
  {
    requiredPermissions: "canManageSchedules",
  },
)

export const loadDeviceSchedulesAsync = createHandler(
  "LoadDeviceSchedulesForFarm",
)
export const loadUpcomingActionsForDeviceAsync = createHandler(
  "LoadUpcomingActionsForDevice",
)
export const registerPushTokenAsync = createHandler("RegisterPushToken")
export const unregisterPushTokenAsync = createHandler("UnregisterPushToken")
export const transferFarmOwnershipAsync = createHandler(
  "TransferFarmOwnership",
  {
    requiredPermissions: "canManageFarmAccount",
  },
)
export const deactivateFarmAsync = createHandler("DeactivateFarm", {
  requiredPermissions: "canManageFarmAccount",
})
export const leaveFarmAsync = createHandler("LeaveFarm")
export const deleteUserAccountAsync = createHandler("DeleteUserAccount", {
  demoMode: { allowRequest: true },
})

export const updateUserAccountAsync = createHandler("UpdateUserAccount")
export const updateDeviceConnectionAsync = createHandler(
  "UpdateDeviceConnection",
)
export const createDeviceCommentAsync = createHandler("CreateDeviceComment")
export const clearCommentsForDeviceAsync = createHandler(
  "ClearCommentsForDevice",
)
export const getAllDevicesByFarmAsync = createHandler("GetAllDevicesByFarm", {
  thunkOptions: {
    condition: (_, { getState }) => {
      return getIsAdminModeEnabledFromState(getState())
    },
  },
})

export const loadMultifarmAnalyticsAsync = createHandler(
  "LoadMultifarmAnalytics",
  {
    demoMode: {
      allowRequest: true,
    },
  },
)

interface MinDateMaxDateBackend {
  maxDateMs: number
  minDateMs: number
}

interface WithCronString {
  cronScheduleRaw: string
  executionTimeMs?: undefined
  scheduleEndMs: number
  scheduleStartMs: number
}

interface WithExecutionTime {
  cronScheduleRaw?: undefined
  executionTimeMs: number
  scheduleEndMs?: undefined
  scheduleStartMs?: undefined
}

export type DeviceCommentStatus = "active" | "suppressed"
export type MultifarmAnalyticsArgs = MinDateMaxDate & {
  _todoDeleteMe?: never
}
export interface MultifarmAnalyticsResponse {
  _todoDeleteMe?: never
}

export interface AssistantUsage {
  spentUsd: string
  budgetUsd: string
  remainingUsd: string
}

export type AssistantJobStatus = "PENDING" | "RUNNING" | "SUCCESS" | "FAILED"

export interface AssistantConversationSummary {
  id: number
  title: string
  updatedAt: string
}

export interface AssistantVisibleMessage {
  role: "user" | "assistant"
  text: string
}

export interface RequestNameToHandler {
  AcceptTermsOfService: BackendRequest
  AskAssistant: BackendRequest<
    { question: string; conversationId?: number | null },
    | {
        status: "OK"
        jobId: number
        conversationId: number
        jobStatus: AssistantJobStatus
      }
    | { status: "BUDGET_EXCEEDED"; usage: AssistantUsage }
    | { status: "UNAVAILABLE" }
  >
  GetAssistantJob: BackendRequest<
    { jobId: number },
    {
      jobId: number
      status: AssistantJobStatus
      resultText: string
      error: string
      conversationId: number | null
    }
  >
  GetAssistantUsage: BackendRequest<undefined, AssistantUsage>
  ListAssistantConversations: BackendRequest<
    { limit?: number },
    ListResponse<AssistantConversationSummary>
  >
  GetAssistantConversation: BackendRequest<
    { conversationId: number },
    { id: number; title: string; messages: AssistantVisibleMessage[] }
  >
  GetAssistantReport: BackendRequest<
    { reportId: number },
    {
      reportId: number
      title: string
      filename: string
      contentBase64: string
    }
  >
  ActivateFields: BackendRequest<{
    ids: number[]
  }>
  AddDevice: BackendRequest<
    {
      inputMode: AddDeviceInputMode
      value: string
    },
    AddDeviceResponse
  >
  ArchiveFields: BackendRequest<{
    ids: number[]
  }>
  CallNamedDeviceAction: BackendRequest<
    Models.DeviceIdProps & {
      actionId: number
      arguments: Record<string, boolean | number | string | null | undefined> | null
    },
    {
      result?: Models.DeviceFunctionCall | null | undefined
    }
  >
  ChangeFarmLocation: BackendRequest<{
    activeFarmId: number
    locationGeojson: Geo.PointGeoJson
  }>
  ClearCommentsForDevice: BackendRequest<{ deviceId: string }>
  ClearDeviceUnreachable: BackendRequest<
    Models.DeviceIdProps,
    {
      deviceId: string
      unreachableUntil: string | null
      consecutiveTimeoutCount: number
    }
  >
  CreateCenterPivot: BackendRequest<
    {
      name: string
      make?: string | null
      model?: string | null
      pivotCenterGpsLocation?: Geo.PointGeoJson | null
      pivotRadiusMeters?: number | null
      boundaryStartDegrees?: number | null
      boundaryEndDegrees?: number | null
      pivotSlots?: Array<{ name: string; angleDegrees: number }>
    },
    CenterPivotResponse
  >
  CreateLinearIrrigator: BackendRequest<
    {
      name: string
      make?: string | null
      model?: string | null
      pathGeometry?: Geo.LineStringGeoJson | null
      spanHeadingDegrees?: number | null
      spanWidthMeters?: number | null
    },
    LinearIrrigatorResponse
  >
  ControlCenterPivot: BackendRequest<
    {
      centerPivotId: number
      enable: boolean
      // Required when enable=true; ignored when enable=false.
      direction?: "clockwise" | "counter_clockwise"
      targetSpeedMetersPerHour?: number
      destinationAngleDegrees?: number
      startAngleDegrees?: number
      destinationSlotId?: number | null
      /** How the run's speed is commanded.
       *
       *  - `percent`    positions the speed output at an absolute
       *                 percent and leaves it there. The only option
       *                 for a pivot without speed feedback.
       *  - `hold_speed` hands the controller a target in m/min and
       *                 lets its regulator drive the output until the
       *                 measured speed matches. Requires the pivot to
       *                 have both speed feedback and PID gains.
       *
       * Omit to let the backend pick: closed loop when the pivot can,
       * percent otherwise. Asking for `hold_speed` on a pivot that
       * can't do it is an error rather than a silent downgrade. */
      speedCommandMode?: "percent" | "hold_speed"
      // Optional pass-through device-state controls.
      wet?: number | null
      endGun?: number | null
    },
    {
      plan: {
        id: number
        center_pivot_id: number
        destination_slot_id: number | null
        destination_angle_degrees: number
        direction: string
        target_speed_meters_per_hour: number
        start_angle_degrees: number
        monitor_activity_id: number
        expires_at: string
        status: string
        finished_at: string | null
        finish_detail: string | null
        create_date: string
      } | null
      /** Set on the open-loop path only. */
      speed_percent?: number | null
      /** Set on the closed-loop path only — the m/min target handed to
       * the controller's regulator. */
      target_speed_m_min?: number | null
      is_closed_loop?: boolean
    }
  >
  ControlLinearIrrigator: BackendRequest<
    {
      linearIrrigatorId: number
      enable: boolean
      // Required when enable=true; ignored when enable=false.
      direction?: "forward" | "reverse"
      targetSpeedMetersPerHour?: number
      destinationDistanceMeters?: number
      startDistanceMeters?: number
      destinationSlotId?: number | null
      wet?: number | null
      endGun?: number | null
    },
    {
      plan: {
        id: number
        linear_irrigator_id: number
        destination_slot_id: number | null
        destination_distance_meters: number
        direction: string
        target_speed_meters_per_hour: number
        start_distance_meters: number
        monitor_activity_id: number
        expires_at: string
        status: string
        finished_at: string | null
        finish_detail: string | null
        create_date: string
      } | null
      speed_percent?: number
    }
  >
  CompleteSmsVerification: BackendRequest<
    {
      codeString: string
      upnId: number
    },
    {
      status: SmsVerificationStatusCode
    }
  >
  CreateConfiguration: BackendRequest<
    Models.DeviceIdProps & {
      [key in MutableSensorName]: SensorConfig<key> | null | undefined
    } & {
      deviceInstallationType: InstallationType
      deviceName: string
      linearPathStopsCoordinates: Geo.Coordinates[] | null | undefined
      linearPathStopsLabels: string[] | null | undefined
      linearSpanHeadingDegrees: number | null | undefined
      linearSpanWidthMm: number | null | undefined
      pivotCenterGpsLocation?: Geo.PointGeoJson | null
      pivotRadiusMeters?: number | null
      boundaryStartDegrees?: number | null
      boundaryEndDegrees?: number | null
    },
    DeviceConfiguration
  >
  CreateCustomAutomation: BackendRequest<
    {
      namedDeviceActionId: number
      notify: boolean
      sourceDeviceId: string
      sourceSensor: SensorName
      sourceSensorStateCurrent: SensorState
      sourceSensorStatePrevious: SensorState
      targetDeviceId: string | null
      delaySeconds: number | null
    },
    Models.EventActionTrigger
  >
  CreateCustomNotification: BackendRequest<
    {
      sourceDeviceId: string
      sourceSensor: SensorName
      sourceSensorStateCurrent: SensorState
      sourceSensorStatePrevious: SensorState
    },
    Models.EventActionTrigger
  >
  CreateDeviceActionSchedule: BackendRequest<
    Union<WithCronString, WithExecutionTime> & {
      deviceId: string
      executionTimezone: string
      isEnabled: boolean
      namedDeviceActionId: number
      shouldSyncSchedule: boolean
    },
    FormValidationInfo<Models.DeviceActionSchedule>
  >
  CreateDeviceComment: BackendRequest<
    Models.DeviceIdProps & { contentText: string },
    Models.DeviceComment
  >
  CreateDeviceShareCode: BackendRequest<
    Models.DeviceIdProps & {
      permissions: DevicePermissions
    },
    Models.DeviceShareCode
  >
  CreateFarm: BackendRequest<
    FarmAccountProperties & {
      gpsLocation: Geo.PointGeoJson | null
    },
    { farm: FarmWithPermissions }
  >
  CreateFarmJoinCode: BackendRequest<
    { permissions: FarmUserPermissions },
    Models.FarmJoinCode
  >
  CreateField: BackendRequest<
    {
      fieldName: string
      fieldType: FieldType
      polygon: Geo.PolygonGeoJson
      rowDirectionAzimuthDegrees: number
    },
    Models.FieldDatabaseValue
  >

  CreatePhoneNumberInternational: BackendRequest<
    { phoneNumberE164Str: string },
    Models.PhoneNumber
  >

  CreatePumpOnForDurationSchedule: BackendRequest<
    Union<WithCronString, WithExecutionTime> & {
      deviceId: string
      displayName: string
      durationMs: number
      executionTimezone: string | null | undefined
    },
    FormValidationInfo<Models.DeviceActionSchedule>
  >
  CreateScheduledDeviceAction: BackendRequest<
    {
      deviceId: string
      executeAtTsMilliseconds: number
      isScheduled: boolean
      namedDeviceActionId: number
      shouldSyncSchedule: boolean
    },
    Models.ScheduledDeviceAction
  >
  CreateSupportTicket: BackendRequest<
    {
      body: string
      email: string
      subject: string
    },
    {
      request?: unknown
      requestSkipped?: boolean | null
      ticket?: unknown
    }
  >
  CustomizeNotificationString: BackendRequest<{
    notificationString: string
    triggerId: number
  }>
  DeactivateFarm: BackendRequest<
    { confirmationText: string },
    { status: "SUCCESS" }
  >
  DeleteDeviceActionSchedule: BackendRequest<{
    deviceActionScheduleId: number
    shouldSyncSchedule: boolean
  }>
  DeleteCenterPivot: BackendRequest<
    { centerPivotId: number },
    { success: boolean; id: number }
  >
  DeleteLinearIrrigator: BackendRequest<
    { linearIrrigatorId: number },
    { success: boolean; id: number }
  >
  DeleteEventActionTriggers: BackendRequest<
    Models.DeviceIdProps & {
      ids: number[]
      kind: TriggerFilterKey
    }
  >
  DeleteField: BackendRequest<Models.FieldIdProps>
  DeletePhoneNumber: BackendRequest<{
    upnId: number
  }>
  DeleteUserAccount: BackendRequest<
    { confirmationText: string },
    { status: "SUCCESS" }
  >
  GenerateSmsVerificationCode: BackendRequest<
    {
      upnId: number
    },
    {
      body: string
      to_: string
    }
  >
  GetAllDevicesByFarm: BackendRequest<
    {
      maxDateMs: number
      minDateMs: number
    },
    {
      [farmId: number]: {
        events: Array<
          Pick<
            DeviceConfiguration,
            | "deviceId"
            | "deviceInstallationType"
            | "deviceName"
            | "firmwareVersion"
            | "hardwareGeneration"
          > & {
            deviceLocation: Geo.PointGeoJson | null | undefined
          }
        >
        farmId: number
        farmLocation: Geo.PointGeoJson | null
        farmName: string
      }
    }
  >
  GetCenterPivot: BackendRequest<
    { centerPivotId: number },
    CenterPivotResponse
  >
  GetFarmPermissionsForUser: BackendRequest<
    {
      targetUserId: string
    },
    FarmUserPermissions
  >
  GetFarmsBorrowingDevice: BackendRequest<
    Models.DeviceIdProps,
    ListResponse<Models.DeviceFarmAssociation>
  >
  GetLastEventForDevice: BackendRequest<
    Models.DeviceIdProps & { previousEventId: number | null },
    Models.DeviceIdProps &
      Union<
        Models.CodaDeviceAliasProps & {
          deviceName?: string
          event: Models.DeviceEvent
          status: "SUCCESS"
        },
        {
          event?: undefined
          status: "NO EVENTS" | "NOT NEW"
        }
      >
  >

  GetNotifications: BackendRequest<
    {
      activeFarmId: number | null
    },
    ListResponse<Models.NotificationEntity>
  >
  InvalidateDeviceShareCode: BackendRequest<
    Models.DeviceIdProps & {
      codeId: number
    },
    Models.DeviceShareCode
  >
  LoadActiveUser: BackendRequest<
    undefined,
    {
      appFeatureFeedback: Array<{
        featureName: string | null | undefined
        isLiked?: boolean | null
      }>
      farms: FarmAccount[]
      phoneNumbers: Models.PhoneNumber[]
      userData: Models.UserAccountData
    }
  >
  LinkCenterPivotController: BackendRequest<
    { deviceId: string; centerPivotId: number },
    { success: boolean; deviceId: string; centerPivotId: number }
  >
  LinkCenterPivotMonitor: BackendRequest<
    { deviceId: string; centerPivotId: number },
    { success: boolean; deviceId: string; centerPivotId: number }
  >
  LinkDeviceToCenterPivot: BackendRequest<
    { deviceId: string; centerPivotId: number },
    { success: boolean; deviceId: string; centerPivotId: number }
  >
  LinkDeviceToLinearIrrigator: BackendRequest<
    { deviceId: string; linearIrrigatorId: number },
    { success: boolean; deviceId: string; linearIrrigatorId: number }
  >
  LeaveFarm: BackendRequest<undefined, { status: "SUCCESS" }>
  ListCenterPivots: BackendRequest<
    undefined,
    ListResponse<CenterPivotPayload>
  >
  LoadApp: BackendRequest<
    Models.LoadDeviceActivityParams,
    Models.LoadActiveFarmResponse
  >
  LoadDeviceActions: BackendRequest<
    Models.DeviceIdProps,
    ListResponse<Models.NamedDeviceAction> & {
      ioExpanderSensorConfigs: Array<{
        id: number
        sensorType: string
        lastOutputs?: {
          output0: boolean | null
          output1: boolean | null
          output2: boolean | null
          output3: boolean | null
          output4: boolean | null
          output5: boolean | null
          output6: boolean | null
          output7: boolean | null
        }
      }>
      centerPivotSensorConfigs: CenterPivotSensorConfig[]
    }
  >
  LoadDeviceActivity: BackendRequest<
    Models.LoadDeviceActivityParams,
    Models.LoadDeviceActivityResponse
  >
  LoadDeviceFlowHistoryData: BackendRequest<
    MinDateMaxDateBackend & Models.DeviceIdProps,
    ListResponse<Models.DeviceTimeSeriesUnion>
  >
  LoadDeviceFunctionCalls: BackendRequest<
    Models.DeviceIdProps,
    ListResponse<Models.DeviceFunctionCall>
  >
  LoadDeviceSchedulesForFarm: BackendRequest<
    {
      dateMsMax?: number | null
      dateMsMin?: number | null
      deviceIds?: string[] | null
    },
    {
      activeDeviceActionSchedules: Models.DeviceActionSchedule[]
      pastScheduledDeviceActions: Models.ScheduledDeviceAction[]
    }
  >
  LoadUpcomingActionsForDevice: BackendRequest<
    Models.DeviceIdProps,
    { upcomingSchedules: Models.DeviceActionSchedule[] }
  >
  // LoadDeviceProfile: LoadDeviceProfile;
  LoadDeviceTimeseriesData: BackendRequest<
    Models.DeviceIdProps & {
      maxDateMs: number
      maxEventCount?: number
      minDateMs: number
    },
    ListResponse<Models.DeviceTimeSeriesUnion>
  >

  LoadEventsForDevice: BackendRequest<
    Models.DeviceIdProps & { limit: number; offset: number },
    ListResponse<Models.DeviceEvent>
  >
  LoadFarmTimelineEvents: BackendRequest<
    {
      minDate?: string | null
      maxDate?: string | null
    },
    ListResponse<Models.FarmTimelineEvent>
  >
  LoadFieldIrrigationHistory: BackendRequest<
    Models.FieldIdProps & {
      options: MinDateMaxDateBackend & {
        useHistoricalNozzle?: boolean
      }
    },
    ListResponse<Models.ReelRun>
  >
  LoadFieldIrrigationHeatmap: BackendRequest<
    Models.FieldIdProps & {
      options?: MinDateMaxDateBackend & {
        useHistoricalNozzle?: boolean
      }
      resolutionM?: number
      /**
       * false skips server-side raster generation (history screen renders
       * from cells with user-chosen windows; rasterising those would churn
       * the per-field cache the status map relies on).
       */
      includeRaster?: boolean
      /**
       * false skips loading runs and computing the per-cell grid (status
       * map renders the raster instead) — a raster cache hit then costs
       * one summary query and ~1 KB instead of ~600 KB of cells.
       */
      includeCells?: boolean
    },
    Models.IrrigationHeatmapGrid
  >
  LoadCenterPivotIrrigationHeatmap: BackendRequest<
    {
      pivotId: number
      options?: MinDateMaxDateBackend
    },
    Models.PivotIrrigationHeatmapGrid
  >
  ExpireFarmViewCache: BackendRequest<undefined, { status: "SUCCESS" }>
  ExportFieldIrrigationHistory: BackendRequest<
    Models.FieldIdProps & {
      options: MinDateMaxDateBackend & {
        useHistoricalNozzle?: boolean
      }
      unitSystem: "metric" | "imperial"
      tzOffsetMinutes: number
    },
    { data: string }
  >
  LoadFlowMeteringAnalytics: BackendRequest<
    MinDateMaxDate & {
      source: FlowAnalyticsSource
    },
    FlowAnalyticsData | null
  >
  LoadFlowMeteringReport: BackendRequest<
    {
      maxDateMs: number
      minDateMs: number
    },
    { data: string }
  >
  LoadMultifarmAnalytics: BackendRequest<
    MultifarmAnalyticsArgs,
    MultifarmAnalyticsResponse
  >
  LoadOtherFarmUsers: BackendRequest<
    undefined,
    ListResponse<Models.OtherFarmUser>
  >
  PrepareDemoEnvironment: BackendRequest<undefined, { activeFarmId: number }>
  RemoveDeviceFromFarm: BackendRequest<Models.DeviceIdProps>
  RenameDevice: BackendRequest<
    Pick<DeviceConfiguration, "deviceId" | "deviceName">,
    { id: number }
  >
  RenameField: BackendRequest<
    Models.FieldIdProps &
      Pick<Models.FarmField, "fieldName" | "labelRotationDegrees">,
    Models.FarmField
  >
  ReplaceDevice: BackendRequest<
    {
      codaDeviceAliasNew: string
      deviceIdOld: string
    },
    AddDeviceResponse
  >
  RequestUpdateFromDevice: BackendRequest<
    Models.DeviceIdProps & {
      // Need this for request matching purposes only, this will never be defined
      actionId?: undefined
    },
    {
      result?: Models.DeviceFunctionCall | null | undefined
    }
  >
  ResetDevice: BackendRequest<
    Models.DeviceIdProps,
    {
      result?: Models.DeviceFunctionCall | null | undefined
    }
  >
  RestoreConfigurationDefaults: BackendRequest<
    Models.DeviceIdProps,
    DeviceConfiguration
  >
  SeedTestDatabase: BackendRequest<
    SeedDatabaseOptions,
    {
      deviceData: GetResponseDataType<"LoadDeviceActivity"> | null | undefined
      farmData: GetResponseDataType<"LoadApp"> | null | undefined
      userData: GetResponseDataType<"LoadActiveUser"> | null | undefined
    }
  >
  SaveCenterPivotControllerConfig: BackendRequest<
    { deviceId: string } & Partial<Omit<CenterPivotSensorConfig, "id" | "sensorType">>,
    CenterPivotSensorConfig
  >
  SendDeviceTestNotification: BackendRequest<
    Models.DeviceIdProps,
    { deviceId: string; nRecipients: number; scope: string }
  >
  SendFarmTestNotification: BackendRequest<
    undefined,
    { nRecipients: number; scope: string }
  >
  SendTestNotification: BackendRequest<
    undefined,
    { nRecipients: number; scope: string }
  >
  SendTestSms: BackendRequest<{ upnId: number }>
  SetDeviceLocationPermanent: BackendRequest<
    Models.DeviceIdProps & { locationPermanent: Geo.PointGeoJson | null }
  >
  SetDeviceMuteStatus: BackendRequest<
    Models.DeviceIdProps & { muted: boolean },
    {
      deviceId: string
      notificationsMuted: boolean
      notificationsMutedAt?: string | null
      notificationsMutedByUserEmail?: string | null
    }
  >
  SetFieldRowDirection: BackendRequest<Models.FieldIdProps & { value: number }>
  SetLanguagePreferenceOverrideForUser: BackendRequest<{
    languageCode: SupportedAppLanguageCode
  }>
  SetNotificationChannelPreference: BackendRequest<
    { channelPreference: Models.NotificationChannelPreference },
    { notificationChannelPreference: Models.NotificationChannelPreference }
  >
  SetNotificationReadStatus: BackendRequest<{ ids: number[] }>
  SetNotificationStatusForPhoneNumber: BackendRequest<{
    nextStatus: NotificationsStatus
    upnId: number
  }>
  SetReelDistance: BackendRequest<
    {
      deviceId: string
      distanceMmCurrent: number
      distanceMmMax: number
    },
    Models.DeviceFunctionCall
  >
  SetReelPumpPairRelayToggleDuration: BackendRequest
  SetReelRunApplicationType: BackendRequest<
    Models.ReelRunIdProps & {
      value: ApplicationType
    }
  >
  SetReelRunDirection: BackendRequest<
    Models.ReelRunIdProps & {
      value: number
    }
  >
  SetReelRunField: BackendRequest<
    Models.ReelRunIdProps & {
      fieldId: number | null
    }
  >
  SubmitAppFeatureFeedback: BackendRequest<{
    featureName: string
    feedbackText?: string

    isLiked: boolean | null | undefined
  }>

  ToggleNotificationsFromDevices: BackendRequest<
    {
      action: "ENABLE" | "SUPPRESS"
      ids: string[]
    },
    ListResponse<Models.DeviceUserAssociation>
  >
  TransferFarmOwnership: BackendRequest<
    { targetUserId: string },
    { status: "SUCCESS"; newOwnerUserId: string }
  >
  UnlinkDeviceFromCenterPivot: BackendRequest<
    { deviceId: string },
    { success: boolean; deviceId: string }
  >
  UnlinkDeviceFromLinearIrrigator: BackendRequest<
    { deviceId: string },
    { success: boolean; deviceId: string }
  >
  UpdateCenterPivot: BackendRequest<
    {
      centerPivotId: number
      name?: string
      make?: string | null
      model?: string | null
      pivotCenterGpsLocation?: Geo.PointGeoJson | null
      pivotRadiusMeters?: number | null
      boundaryStartDegrees?: number | null
      boundaryEndDegrees?: number | null
      endGunThrowRadiusMeters?: number | null
      averageFlowRateLpm?: number | null
      maxSpeedMetersPerHour?: number | null
      minSpeedMetersPerHour?: number | null
      initialSpeedPercent?: number | null
      /** PID tuning for controller-side closed-loop speed regulation.
       * Pass 0 for a gain to clear it; clearing the proportional gain
       * drops the pivot back to running by percent. */
      speedPidProportionalGain?: number | null
      speedPidIntegralGain?: number | null
      speedPidDerivativeGain?: number | null
      /** Pass 0 to disable notify-on-stop (stored as null). */
      stoppedDetectionMinDistanceMeters?: number | null
      /** Notify-on-stop horizon in minutes; must be positive. */
      stoppedDetectionDurationMinutes?: number
    },
    CenterPivotResponse
  >
  UpdateLinearIrrigator: BackendRequest<
    {
      linearIrrigatorId: number
      name?: string
      make?: string | null
      model?: string | null
      pathGeometry?: Geo.LineStringGeoJson | null
      spanHeadingDegrees?: number | null
      spanWidthMeters?: number | null
      averageFlowRateLpm?: number | null
      maxSpeedMetersPerHour?: number | null
      minSpeedMetersPerHour?: number | null
      initialSpeedPercent?: number | null
    },
    LinearIrrigatorResponse
  >
  SetCenterPivotSlots: BackendRequest<
    {
      centerPivotId: number
      slots: Array<{
        id?: number
        name: string
        location: Geo.PointGeoJson
      }>
    },
    CenterPivotResponse
  >
  SetLinearIrrigatorSlots: BackendRequest<
    {
      linearIrrigatorId: number
      slots: Array<{
        id?: number
        name: string
        location: Geo.PointGeoJson
      }>
    },
    LinearIrrigatorResponse
  >

  // Irrigation Plan Handlers
  CreateIrrigationPlan: BackendRequest<
    {
      centerPivotId: number
      name: string
      description?: string | null
      sectors?: IrrigationPlanSectorInput[]
      boundaryActions?: IrrigationPlanBoundaryActionInput[]
    },
    IrrigationPlanResponse
  >
  UpdateIrrigationPlan: BackendRequest<
    {
      planId: number
      name?: string
      description?: string | null
    },
    IrrigationPlanResponse
  >
  DeleteIrrigationPlan: BackendRequest<
    { planId: number },
    { success: boolean; id: number }
  >
  GetIrrigationPlan: BackendRequest<{ planId: number }, IrrigationPlanResponse>
  ListIrrigationPlans: BackendRequest<
    { centerPivotId?: number },
    ListResponse<IrrigationPlanSummaryResponse>
  >
  CreatePlanSector: BackendRequest<
    {
      planId: number
      startAngleDegrees: number
      endAngleDegrees: number
      name?: string | null
      priority?: number
      irrigationMode?: string
      depthMode?: string
      speedPercent?: number | null
      applicationDepthInches?: number | null
      waterState?: string
      endGunState?: string
      direction?: string
      color?: string
    },
    PlanSectorResponse
  >
  UpdatePlanSector: BackendRequest<
    {
      sectorId: number
      name?: string | null
      startAngleDegrees?: number
      endAngleDegrees?: number
      priority?: number
      irrigationMode?: string
      depthMode?: string
      speedPercent?: number | null
      applicationDepthInches?: number | null
      waterState?: string
      endGunState?: string
      direction?: string
      color?: string
    },
    PlanSectorResponse
  >
  DeletePlanSector: BackendRequest<
    { sectorId: number },
    { success: boolean; id: number }
  >
  ReplaceIrrigationPlanSectors: BackendRequest<
    {
      planId: number
      sectors: IrrigationPlanSectorInput[]
    },
    {
      plan: IrrigationPlanResponse
      sectors: PlanSectorResponse[]
    }
  >

  // Pivot End Gun Table Handlers
  ListPivotEndGunTables: BackendRequest<
    { centerPivotId: number },
    ListResponse<PivotEndGunTableResponse>
  >
  CreatePivotEndGunTable: BackendRequest<
    {
      centerPivotId: number
      name: string
      isActive?: boolean
      sectors?: PivotEndGunTableSectorInput[]
    },
    PivotEndGunTableResponse
  >
  UpdatePivotEndGunTable: BackendRequest<
    {
      endGunTableId: number
      name?: string
      isActive?: boolean
    },
    PivotEndGunTableResponse
  >
  DeletePivotEndGunTable: BackendRequest<
    { endGunTableId: number },
    { success: boolean; id: number }
  >
  ListPivotEndGunTableSectors: BackendRequest<
    { endGunTableId: number },
    ListResponse<PivotEndGunTableSectorResponse>
  >
  CreatePivotEndGunTableSector: BackendRequest<
    {
      endGunTableId: number
      startAngleDegrees: number
      endAngleDegrees: number
      endGunState?: EndGunState
      sortOrder?: number
    },
    PivotEndGunTableSectorResponse
  >
  UpdatePivotEndGunTableSector: BackendRequest<
    {
      endGunTableSectorId: number
      startAngleDegrees?: number
      endAngleDegrees?: number
      endGunState?: EndGunState
      sortOrder?: number
    },
    PivotEndGunTableSectorResponse
  >
  DeletePivotEndGunTableSector: BackendRequest<
    { endGunTableSectorId: number },
    { success: boolean; id: number }
  >
  ReplacePivotEndGunTableSectors: BackendRequest<
    {
      endGunTableId: number
      sectors: PivotEndGunTableSectorInput[]
    },
    {
      endGunTable: PivotEndGunTableResponse
      sectors: PivotEndGunTableSectorResponse[]
    }
  >
  CreateBoundaryAction: BackendRequest<
    {
      planId: number
      triggerAngleDegrees: number
      actionType: string
      name?: string | null
      triggerDirection?: string
      targetAngleDegrees?: number | null
      delaySeconds?: number
      customActionId?: number | null
    },
    BoundaryActionResponse
  >
  UpdateBoundaryAction: BackendRequest<
    {
      actionId: number
      name?: string | null
      triggerAngleDegrees?: number
      triggerDirection?: string
      actionType?: string
      targetAngleDegrees?: number | null
      delaySeconds?: number
      customActionId?: number | null
    },
    BoundaryActionResponse
  >
  DeleteBoundaryAction: BackendRequest<
    { actionId: number },
    { success: boolean; id: number }
  >
  ActivateIrrigationPlan: BackendRequest<
    { planId: number },
    IrrigationPlanResponse
  >
  PauseIrrigationPlan: BackendRequest<{ planId: number }, IrrigationPlanResponse>
  ResumeIrrigationPlan: BackendRequest<
    { planId: number },
    IrrigationPlanResponse
  >
  StopIrrigationPlan: BackendRequest<{ planId: number }, IrrigationPlanResponse>
  GetPlanExecutionLogs: BackendRequest<
    { planId: number; limit?: number },
    ListResponse<PlanExecutionLogResponse>
  >

  UpdateDeviceConnection: BackendRequest<{
    connectionId: number
    notify?: boolean
  }>
  UpdateFarmUserPermissions: BackendRequest<{
    permissions: FarmUserPermissions
    targetUserId: string
  }>
  UpdateFieldBoundary: BackendRequest<
    Models.FieldIdProps & {
      polygon: Geo.PolygonGeoJson
    }
  >
  UpdateScheduledDeviceAction: BackendRequest<
    Union<
      {
        executeAtTsMilliseconds: number
        isScheduled?: boolean
      },
      {
        executeAtTsMilliseconds?: number
        isScheduled: boolean
      }
    > & {
      scheduledDeviceActionId: number
    },
    Models.ScheduledDeviceAction
  >
  RegisterPushToken: BackendRequest<
    { token: string; platform: string | null; deviceId: string | null },
    { status: string; created: boolean }
  >
  UnregisterPushToken: BackendRequest<
    { token: string },
    { status: string; deactivated: boolean }
  >
  UpdateUserAccount: BackendRequest<
    {
      nameFamily?: string
      nameGiven?: string
      termsOfServiceAccepted?: boolean
    },
    Models.UserAccountData
  >
  UserJoinFarmWithCode: BackendRequest<
    {
      codeString: string
    },
    | {
        codeStatus: "SUCCESS"
        errorMessage?: undefined
        farm: FarmWithPermissions
      }
    | {
        codeStatus: JoinFarmErrorCode
        errorMessage: string
        farm: undefined
      }
  >
}
